import { yupResolver } from '@hookform/resolvers/yup';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import { keys } from 'ramda';
import { useEffect, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { ProgressButton } from 'components';
import { LocalStorageService } from 'api';
import { useLoginMutation, useSignupMutation } from 'store/api/version-api.slice';

import { SIGN_UP_DEFAULT_VALUES } from '../constants';
import SIGN_UP_SCHEMA from '../signUp.schema';
import { styles } from '../styles/signUpRoute.styles';

import AddressInputs from './AddressInputs';
import AlreadyMember from './AlreadyMember';
import ContinueDialog from './ContinueDialog';
import FullnameInputs from './FullnameInputs';
import OrganizationEmailInputs from './OrganizationEmailInputs';
import PasswordInputs from './PasswordInputs';

const localStorageService = new LocalStorageService();

const SignupForm = () => {
	const recaptchaRef = useRef();

	const navigate = useNavigate();

	const [signup, { isLoading: isSignupLoading, data: signupResult = {} }] = useSignupMutation();
	const [login] = useLoginMutation();

	const [successDialog, setSuccessDialog] = useState(false);

	const methods = useForm({ defaultValues: SIGN_UP_DEFAULT_VALUES, resolver: yupResolver(SIGN_UP_SCHEMA) });

	const {
		handleSubmit,
		formState: { errors }
	} = methods;

	const onSubmit = async data => {
		const { email, password1, first_name, last_name, password2, organization, street, city, state, zipcode } = data;
		const formatted_address = `${street}, ${city}, ${state} ${zipcode}`;

		const signupData = {
			first_name,
			last_name,
			email,
			password1,
			password2,
			organization,
			address: formatted_address,
			captcha: recaptchaRef.current.getValue()
		};

		await signup(signupData);
		const response = await login({ username: email, password: password1 });

		localStorageService.setTokens(response.data.access, response.data.refresh);
	};

	const onSuccessClose = () => {
		navigate('/onboarding');
	};

	useEffect(() => {
		if (signupResult.access) setSuccessDialog(true);
	}, [signupResult.access]);

	return (
		<FormProvider {...methods}>
			<form onSubmit={handleSubmit(onSubmit)}>
				<Grid container spacing={1}>
					<FullnameInputs />
					<OrganizationEmailInputs />
					<PasswordInputs />
					<AddressInputs />
					<Grid item xs={3}>
						<ReCAPTCHA
							name="captcha"
							ref={recaptchaRef}
							sitekey={process.env.REACT_APP_reCAPTCHA_SITE_KEY}
							theme="light"
						/>
					</Grid>
					<ProgressButton
						fullWidth
						circleColor={'secondary'}
						button={
							<Button
								type="submit"
								fullWidth
								variant="contained"
								color="primary"
								sx={styles.submit}
								disabled={keys(errors).length > 0}
							>
								Sign Up
							</Button>
						}
						isLoading={isSignupLoading}
					/>
					<AlreadyMember />
					<ContinueDialog isOpen={successDialog} handleClose={onSuccessClose} />
				</Grid>
			</form>
		</FormProvider>
	);
};

export default SignupForm;
